import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Collapse, Stack, Typography } from '@mui/material';
import PaiButton from 'features/ui/base-components/pai-button';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { useUserData } from 'hooks/use-user-data';
import MessageHistoryPanel from './message-history-panel';
import { exportCampaignMessageHistoryToExcel } from './message-history-export';
import { LinkedinContact } from './campaign-statistics-types';

interface Props {
  messageHistory: LinkedinContact[];
  timeZone: string | undefined;
  contactDrawerLoading: boolean;

  handleOpenContactDrawer(linkedInURL: string, profileId?: string, funnelId?: string): () => void;
}

const MessageHistory: React.FC<Props> = ({ messageHistory, timeZone, contactDrawerLoading, handleOpenContactDrawer }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const [expanded, setExpanded] = useState(true);

  const { token } = useUserData();

  const funnelIdsForExport = useMemo(
    () =>
      Array.from(
        new Set(
          messageHistory
            .map((m) => m.funnelId)
            .filter((fid): fid is string => typeof fid === 'string' && fid.length > 0),
        ),
      ),
    [messageHistory],
  );

  const handleToggle = () => {
    setExpanded(!expanded);
  };

  const handleHeaderExport = async (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    await exportCampaignMessageHistoryToExcel(funnelIdsForExport, token);
  };

  return (
    <div className={classes.messageHistory}>
      <Stack
        className={cx(classes.header, expanded ? 'expanded' : undefined)}
        direction={'row'}
        justifyContent={'space-between'}
        alignItems={'center'}
        onClick={handleToggle}
      >
        <Typography className={classes.titleText}>
          {t('messageHistory')}
        </Typography>
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <PaiButton
            text={t('csvExport')}
            size={'small'}
            border={'round'}
            action={handleHeaderExport}
            disabled={messageHistory.length === 0}
          />
          <ExpandMoreIcon className={cx(classes.expandIcon, expanded ? 'expanded' : undefined)} />
        </Stack>
      </Stack>
      <Collapse className={classes.content} in={expanded} unmountOnExit={true}>
        {messageHistory.length > 0 && (
          <MessageHistoryPanel
            messageHistory={messageHistory}
            timeZone={timeZone}
            contactDrawerLoading={contactDrawerLoading}
            handleOpenContactDrawer={handleOpenContactDrawer}
            funnelIdsForExport={funnelIdsForExport}
            showExportButton={false}
          />
        )}
      </Collapse>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  messageHistory: {
    background: '#FFF',
    borderRadius: '12px',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 24px',
    transition: '225ms',
    cursor: 'pointer',
    '&.expanded': {
      borderBottom: `1px solid ${theme.palette.greys.lighter}`,
    },
  },
  titleText: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: 500,
  },
  content: {
    padding: '24px',
  },
  expandIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.primary.main,
    transition: '225ms',
    '&.expanded': {
      transform: 'rotate(180deg)',
    },
  },
}));

export default MessageHistory;
